/**
 * O que o link oferece, e por quanto (F258, refeito pela F260/F261).
 *
 * O tecido é cadastro da loja (F260): cada peça tem uma variação por tecido,
 * cada uma com as suas faixas. O tecido (ou a peça) descontinuado sai do link
 * por padrão — quem gera o link ainda pode marcar.
 *
 * A personalização é um TIPO da loja (F261), com o preço nele: um dado que o
 * cliente preenche em cada peça (nome e/ou número) ou uma opção que ele marca.
 * A peça diz se tem personalização e quais tipos valem para ela.
 *
 * As listas abaixo moram também no contrato e nos CHECKs da 0141.
 */
export const PERSONALIZATION_KINDS = ['dados', 'opcao'];
export const PERSONALIZATION_FIELDS = ['nome', 'numero'];
export const PERSONALIZATION_CHARGES = [
    'por-peca',
    'por-pedido',
    'nenhuma',
];
const isOneOf = (list) => (value) => typeof value === 'string' && list.includes(value);
export const isPersonalizationKind = isOneOf(PERSONALIZATION_KINDS);
export const isPersonalizationCharge = isOneOf(PERSONALIZATION_CHARGES);
export const isPersonalizationField = isOneOf(PERSONALIZATION_FIELDS);
/**
 * A peça entra no link?
 *
 * Link sem peças escolhidas oferece as que não estão descontinuadas; quando a
 * atendente escolheu, a escolha vale — é assim que ela "liga" a descontinuada
 * para o pedido que precisa.
 */
export const offersProduct = (setting, chosenByAttendant) => chosenByAttendant || !setting.discontinued;
/**
 * Os tecidos que o link oferece, na ordem da loja.
 *
 * Vazio é "os que não estão descontinuados"; escolhidos, valem os escolhidos.
 * O tecido apagado já não chega aqui.
 */
export const offeredFabrics = (chosen, fabrics) => fabrics
    .filter((fabric) => chosen.length === 0 ? !fabric.discontinued : chosen.includes(fabric.id))
    .map((fabric) => fabric.id);
/**
 * A variação que vira a linha do pedido.
 *
 * Peça sem tecido marcado (a antiga) vale o mesmo em qualquer tecido: a
 * padrão. Com tecido, o "não sei" do cliente (nulo) é a padrão, e um tecido
 * que a peça não tem é nulo — a peça não existe nesse tecido.
 */
export const variantForFabric = (variants, fabricId) => {
    if (variants.byFabric.size === 0 || fabricId === null)
        return variants.defaultVariantId;
    return variants.byFabric.get(fabricId) ?? null;
};
/** Os tecidos em que a peça existe; nulo é "qualquer um, pelo mesmo preço". */
export const fabricsOf = (variants) => variants.byFabric.size === 0 ? null : [...variants.byFabric.keys()];
const filled = (value) => value !== null && value.trim() !== '';
/** A linha é personalizada pelos campos do tipo: o nome, o número ou os dois. */
export const isPersonalizedRow = (row, fields = PERSONALIZATION_FIELDS) => (fields.includes('nome') && filled(row.name)) ||
    (fields.includes('numero') && filled(row.number));
/**
 * Quanto de cada personalização o pedido cobra.
 *
 * - O tipo `dados` conta as peças preenchidas nos campos dele, nas peças em
 *   que vale.
 * - A opção conta as peças das peças que a marcaram.
 * - `por-pedido` é uma vez só, se alguma peça usou; `nenhuma` não vira linha:
 *   volta em `noted`, para a observação do pedido.
 */
export const personalizationCharges = (items, types) => {
    const quantities = new Map();
    const noted = new Set();
    const add = (type, pieces) => {
        if (pieces <= 0)
            return;
        if (type.charge === 'nenhuma') {
            noted.add(type.id);
            return;
        }
        quantities.set(type.id, type.charge === 'por-pedido' ? 1 : (quantities.get(type.id) ?? 0) + pieces);
    };
    for (const item of items) {
        const pieces = item.pieces ?? item.rows.reduce((sum, row) => sum + row.quantity, 0);
        for (const typeId of item.typeIds) {
            const type = types.get(typeId);
            if (type === undefined)
                continue;
            if (type.kind === 'dados') {
                add(type, item.rows
                    .filter((row) => isPersonalizedRow(row, type.fields))
                    .reduce((sum, row) => sum + row.quantity, 0));
            }
            else if (item.options.includes(typeId)) {
                add(type, pieces);
            }
        }
    }
    return {
        lines: [...quantities].map(([typeId, quantity]) => ({ typeId, quantity })),
        noted: [...noted],
    };
};
/** O tipo `dados` da peça: o que define as colunas de nome e número. */
export const dataTypeOf = (typeIds, types) => typeIds
    .map((id) => types.get(id))
    .find((type) => type?.kind === 'dados') ?? null;
//# sourceMappingURL=offer.js.map